// Surface the zone-match classification on producer-facing analytics.
// The runtime RPC (update_crew_location) writes one of:
//   'strict'          — the GPS point was strictly inside the polygon.
//   'within_accuracy' — the point was outside but within the ping's
//                       reported accuracy of the polygon edge.
//   'no_presence'     — there were no presence_log rows at all in
//                       the assignment window (crew was somewhere
//                       else, or GPS dropped). Distinct from
//                       Unknown.
//   NULL / 'unknown'  — pre-fallback rows whose backfill couldn't
//                       reconstruct a match.
//
// The producer needs to distinguish these so a "compliance %" of 86%
// computed from drift fallback isn't mistaken for the same number
// computed from strict matches, AND a 0% with "no_presence" isn't
// confused with 0% on missing metadata.

import { cn } from "@/lib/utils";

export type MatchSource = "strict" | "within_accuracy" | "no_presence" | null | undefined;

interface Props {
  source: MatchSource;
  distanceM?: number | null;
  className?: string;
}

export function MatchConfidenceBadge({ source, distanceM, className }: Props) {
  if (source === "strict") {
    return (
      <span
        data-testid="match-confidence-badge"
        title="GPS point was strictly inside the stage polygon."
        className={cn(
          "inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium",
          "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200",
          className,
        )}
      >
        High
      </span>
    );
  }

  if (source === "within_accuracy" && typeof distanceM === "number") {
    const meters = Math.round(distanceM);
    return (
      <span
        data-testid="match-confidence-badge"
        title={`GPS point was ${meters}m outside the stage polygon, within the ping's reported accuracy. Likely drift.`}
        className={cn(
          "inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium",
          "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200",
          className,
        )}
      >
        {meters}m
      </span>
    );
  }

  if (source === "no_presence") {
    return (
      <span
        data-testid="match-confidence-badge"
        title="No GPS presence at the assigned stage during this slot. Crew was elsewhere or GPS dropped."
        className={cn(
          "inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium",
          "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200",
          className,
        )}
      >
        Not seen
      </span>
    );
  }

  return (
    <span
      data-testid="match-confidence-badge"
      title="Match metadata not recorded — predates the analytics-aware classifier."
      className={cn(
        "inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium",
        "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
        className,
      )}
    >
      Unknown
    </span>
  );
}
